import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from "react";
import {
  Grid,
  Box,
  Typography,
  Tooltip,
  Button,
  CircularProgress,
} from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import TextFieldInput from "../../../components/TextFieldInput";
import SelectInput from "../../../components/SelectInput";
import CommonButton from "../../../shared-components/common-button/CommonButton";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import SearchableAutoComplete from "../../../components/SearchableAutoComplete";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { route } from "../../../services/apiRoutes";
import AddAuthorizedSignatoryButton from "../components/AddAuthorizedSignatoryButton";
import {
  fetchCompanyDetails,
  getApplicantDetails,
  resetGstDetailsData,
  setActiveStep,
  setApplicantActiveStep,
  setApplicantTotal,
  setEditPreviewApplicant,
  setKycVerificationActiveStep,
  setShowSaveAndPreview,
  setStepsCompleted,
  updateApplicantDetailsForm,
} from "../new-booking-form.slice";
import SwitchButton from "../../../shared-components/switch-button/SwitchButton";
import UploadInput from "../../../components/UploadInput";
import UploadPopup from "../../../shared-components/upload-popup/UploadPopup";
import {
  getErrorMessage,
  getLastStep,
  removeBlobImages,
} from "../../../utilis/common-funtions";
import {
  BRANCH_OPTIONS,
  OCCUPATION,
  OTHERS,
} from "../../../config/constant";
import SnackbarAlert from "../../../shared-components/snackbar-alert/SnackbarAlert";
import warningIcon from "../../../assets/icons/ic-solar_warning-triangle-bold.svg";
import GoogleMapsAutocomplete from "../../../shared-components/google-maps-autocomplete/GoogleMapsAutocomplete";

const ProfessionalDetails = forwardRef(({ applicantNo }, ref) => {
  const dispatch = useDispatch();
  const { oppId } = useParams();
  const history = useHistory();
  const { t } = useTranslation();
  const [snackbarConfig, setSnackbarConfig] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const [showUploadGSTPopup, setShowUploadGSTPopup] = useState(false);
  const {
    activeStep,
    masterData,
    applicantData,
    applicantActiveStep,
    stepsCompleted,
    applicantTotal,
    gstDetailsData,
  } = useSelector((state) => state?.newBookingForm);
  const applicantKey = `applicant${applicantNo}`;
  const currentApplicant = applicantData?.[applicantKey];
  const professionalDetails = currentApplicant?.professionalDetails;
  const [showAddress, setShowAddress] = useState(
    !!(
      professionalDetails?.companyAddress || professionalDetails?.companyPinCode
    )
  );
  const [showOfficeAddress, setShowOfficeAddress] = useState(
    !!(professionalDetails?.officeAddress || professionalDetails?.officePinCode)
  );

  const professionalDetailFormik = useFormik({
    initialValues: {
      occupation: "",
      industry: "",
      industryIfOthers: "",
      companyName: "",
      companyAddress: "",
      companyPinCode: "",
      annualIncome: "",
      designation: OTHERS,
      designationIfOthers: "",
      branch: "",
      rank: "",
      officeAddress: "",
      officePinCode: "",
      isGstClaimed: false,
      gstNumber: "",
      gstBusinessName: "",
      gstLegalName: "",
      gstCertificate: [],
      isPhysicalGST: false,
      fetchedGSTDetails: false,
      departmentDivision: "",
    },
    validationSchema: yup.object({
      occupation: yup
        .string()
        .required(t("professionalDetails.validation.occupationRequired")),
      industry: yup.string().when("occupation", {
        is: (occupation) =>
          [OCCUPATION.EMPLOYED_PRIVATE_SECTOR, OCCUPATION.BUSINESS].includes(
            occupation
          ),
        then: (schema) =>
          schema.required(t("professionalDetails.validation.industryRequired")),
        otherwise: (schema) => schema.notRequired(),
      }),
      companyName: yup.string().when("occupation", {
        is: (occupation) =>
          [OCCUPATION.EMPLOYED_PRIVATE_SECTOR, OCCUPATION.BUSINESS].includes(
            occupation
          ),
        then: (schema) =>
          schema
            .matches(
              /^[a-zA-Z0-9\s]+$/,
              t("professionalDetails.validation.companyNamePattern")
            )
            .max(
              100,
              t("authorizedSignatoryDetails.fields.firstName.validation.max")
            )
            .required(t("professionalDetails.validation.companyNameRequired")),
        otherwise: (schema) => schema.notRequired(),
      }),
      companyAddress: yup.string().notRequired(),
      companyPinCode: yup.string().notRequired(),
      annualIncome: yup
        .string()
        .required(t("professionalDetails.validation.annualIncomeRequired")),
      designationIfOthers: yup.string().when("occupation", {
        is: (occupation) =>
          [
            OCCUPATION.EMPLOYED_PRIVATE_SECTOR,
            OCCUPATION.EMPLOYED_GOVERNMENT_SECTOR,
          ].includes(occupation),
        then: (schema) =>
          schema
            .matches(
              /^[a-zA-Z\s]+$/,
              t("professionalDetails.validation.designationIfOthersPattern")
            )
            .max(
              100,
              t("authorizedSignatoryDetails.fields.firstName.validation.max")
            )
            .required(t("professionalDetails.validation.designationRequired")),
        otherwise: (schema) => schema.notRequired(),
      }),
      branch: yup.string().when("occupation", {
        is: OCCUPATION.ARMED_FORCES,
        then: (schema) =>
          schema.required(t("professionalDetails.validation.branchRequired")),
        otherwise: (schema) => schema.notRequired(),
      }),
      rank: yup.string().when("occupation", {
        is: OCCUPATION.ARMED_FORCES,
        then: (schema) =>
          schema
            .matches(
              /^[a-zA-Z\s]+$/,
              t("professionalDetails.validation.rankPattern")
            )
            .max(
              100,
              t("authorizedSignatoryDetails.fields.firstName.validation.max")
            )
            .required(t("professionalDetails.validation.rankRequired")),
        otherwise: (schema) => schema.notRequired(),
      }),
      officeAddress: yup.string().notRequired(),
      officePinCode: yup.string().notRequired(),
      gstNumber: yup.string().when(["isGstClaimed"], {
        is: (isGstClaimed) => isGstClaimed,
        then: (schema) =>
          schema
            .matches(
              /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[0-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/,
              t("companyDetails.validation.matchGstNumber")
            )
            .required(t("professionalDetails.validation.gstNumberRequired")),
        otherwise: (schema) => schema.notRequired(),
      }),
      gstBusinessName: yup.string().when(["isGstClaimed"], {
        is: (isGstClaimed) => isGstClaimed,
        then: (schema) =>
          schema.required(
            t("professionalDetails.validation.gstBusinessNameRequired")
          ),
        otherwise: (schema) => schema.notRequired(),
      }),
      gstLegalName: yup.string().when(["isGstClaimed"], {
        is: (isGstClaimed) => isGstClaimed,
        then: (schema) =>
          schema.required(
            t("professionalDetails.validation.gstLegalNameRequired")
          ),
        otherwise: (schema) => schema.notRequired(),
      }),
      gstCertificate: yup.array().when(["isGstClaimed", "isPhysicalGST"], {
        is: (isGstClaimed, isPhysicalGST) => isGstClaimed && !isPhysicalGST,
        then: (schema) =>
          schema
            .min(1, t("professionalDetails.validation.gstCertificateRequired"))
            .required(
              t("professionalDetails.validation.gstCertificateRequired")
            ),
        otherwise: (schema) => schema.notRequired(),
      }),
      departmentDivision: yup.string().when("occupation", {
        is: (occupation) =>
          [OCCUPATION.EMPLOYED_GOVERNMENT_SECTOR].includes(occupation),
        then: (schema) =>
          schema
            .matches(
              /^[a-zA-Z\s]+$/,
              t("professionalDetails.validation.departmentDivision")
            )
            .max(
              100,
              t("authorizedSignatoryDetails.fields.firstName.validation.max")
            )
            .required(t("professionalDetails.validation.departmentDivision")),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
  });

  useEffect(() => {
    return () => {
      dispatch(resetGstDetailsData());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    professionalDetailFormik.validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [professionalDetailFormik?.values]);

  useEffect(() => {
    dispatch(getApplicantDetails(`/${oppId}`));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (professionalDetails) {
      professionalDetailFormik.setValues({
        occupation: professionalDetails?.occupation || "",
        annualIncome: professionalDetails?.annualIncome || "",
        industry: professionalDetails?.industry || "",
        industryIfOthers: professionalDetails?.industryIfOthers || "",
        companyName: professionalDetails?.companyName || "",
        companyAddress: professionalDetails?.companyAddress || "",
        companyPinCode: professionalDetails?.companyPinCode || "",
        designation: OTHERS,
        designationIfOthers: professionalDetails?.designationIfOthers || "",
        branch: professionalDetails?.branch || "",
        rank: professionalDetails?.rank || "",
        officeAddress: professionalDetails?.officeAddress || "",
        officePinCode: professionalDetails?.officePinCode || "",
        isGstClaimed: professionalDetails?.isGstClaimed ?? false,
        gstNumber: professionalDetails?.gstNumber || "",
        gstBusinessName: professionalDetails?.gstBusinessName || "",
        gstLegalName: professionalDetails?.gstLegalName || "",
        gstCertificate: professionalDetails?.gstCertificate || [],
        isPhysicalGST: professionalDetails?.isPhysicalGST ?? false,
        fetchedGSTDetails: Boolean(professionalDetails?.isGstClaimed),
        departmentDivision: professionalDetails?.departmentDivision || "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicantData]);

  useEffect(() => {
    if (!professionalDetailFormik?.values?.occupation) return;

    const occ = professionalDetailFormik.values.occupation;

    professionalDetailFormik.setValues({
      occupation: occ,
      industry: "",
      industryIfOthers: "",
      companyName: "",
      companyAddress: "",
      companyPinCode: "",
      designation: OTHERS,
      designationIfOthers: "",
      departmentDivision: "",
      branch: "",
      rank: "",
      officeAddress: "",
      officePinCode: "",
      annualIncome: "",
    });

    professionalDetailFormik.setTouched({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [professionalDetailFormik.values.occupation]);

  const handleFetchCompanyDetails = async () => {
    // Validate only GST field
    await professionalDetailFormik.validateField("gstNumber");
    professionalDetailFormik.setFieldTouched("gstNumber", true, false);

    // Stop if invalid
    if (professionalDetailFormik.errors.gstNumber) {
      return;
    }

    setIsButtonLoading(true);
    try {
      const payload = {
        gstNumber: professionalDetailFormik?.values?.gstNumber || "",
        opportunityId: oppId,
        bookingAs: applicantData.bookingAs,
      };
      const res = await dispatch(fetchCompanyDetails(payload)).unwrap();

      if (res?.kycResult) {
        professionalDetailFormik.setValues({
          ...professionalDetailFormik.values,
          gstNumber: res?.kycResult?.gstin || "",
          gstBusinessName: res?.kycResult?.tradeName || "",
          gstLegalName: res?.kycResult?.legalName || "",
          gstCertificate: [],
        });
      }
    } catch (err) {
      console.error("Failed to fetch company details:", err);
      setSnackbarConfig({
        open: true,
        message: getErrorMessage(err),
        severity: "error",
      });
    } finally {
      setIsButtonLoading(false);
    }
  };

  const createProfessionalDetailsPayload = (saveForLaterFlag) => {
    const values = professionalDetailFormik?.values || {};
    const occupation = values?.occupation;

    const payload = {
      opportunityId: oppId,
      applicantNumber: parseInt(applicantNo),
      lastStep: getLastStep(applicantData?.lastStep, activeStep),
      saveForLater: saveForLaterFlag,
      professionalDetails: {
        occupation,
        annualIncome: values?.annualIncome || "",
        isGstClaimed: values?.isGstClaimed === true,
        gstNumber: values?.isGstClaimed ? values?.gstNumber || "" : "",
        gstBusinessName: values?.isGstClaimed
          ? values?.gstBusinessName || ""
          : "",
        gstLegalName: values?.isGstClaimed ? values?.gstLegalName || "" : "",
        gstCertificate: values?.isGstClaimed
          ? values?.gstCertificate || []
          : "",
        isPhysicalGST: values?.isGstClaimed
          ? values?.isPhysicalGST || false
          : false,
      },
    };

    switch (occupation) {
      /** Employee - Private Sector **/
      case OCCUPATION.EMPLOYED_PRIVATE_SECTOR:
        Object.assign(payload.professionalDetails, {
          industry: values?.industry || "",
          industryIfOthers:
            values?.industry === OTHERS
              ? values?.industryIfOthers || OTHERS
              : "",
          companyName: values?.companyName || "",
          companyAddress: values?.companyAddress || "",
          companyPinCode: values?.companyPinCode || "",
          designation: values?.designation || OTHERS,
          designationIfOthers: values?.designationIfOthers || "",
        });
        break;

      /** Employee - Government Sector **/
      case OCCUPATION.EMPLOYED_GOVERNMENT_SECTOR:
        Object.assign(payload.professionalDetails, {
          departmentDivision: values?.departmentDivision || "",
          designation: values?.designation || OTHERS,
          designationIfOthers: values?.designationIfOthers || "",
          officeAddress: values?.officeAddress || "",
          officePinCode: values?.officePinCode || "",
        });
        break;

      /** Armed Forces **/
      case OCCUPATION.ARMED_FORCES:
        Object.assign(payload.professionalDetails, {
          branch: values?.branch || "",
          rank: values?.rank || "",
          officeAddress: values?.officeAddress || "",
          officePinCode: values?.officePinCode || "",
        });
        break;

      /** Business **/
      case OCCUPATION.BUSINESS:
        Object.assign(payload.professionalDetails, {
          industry: values?.industry || "",
          industryIfOthers:
            values?.industry === OTHERS
              ? values?.industryIfOthers || OTHERS
              : "",
          companyName: values?.companyName || "",
          companyAddress: values?.companyAddress || "",
          companyPinCode: values?.companyPinCode || "",
        });
        break;

      /** Retired **/
      case OCCUPATION.RETIRED:
        // Only annualIncome already included
        break;

      /** Homemaker **/
      case OCCUPATION.HOMEMAKER:
        // Only annualIncome already included
        break;

      default:
        break;
    }

    return payload;
  };

  const handleShowUploadGSTCertificate = () => {
    removeBlobImages();
    setShowUploadGSTPopup(true);
  };

  const handleImageValue = (keyName, imgName) => {
    professionalDetailFormik.setFieldValue(keyName, imgName);
  };

  const handleUpdateImageData = (fieldName, imageData) => {
    professionalDetailFormik.setFieldValue(fieldName, imageData);
  };

  const handlePhysicalImage = (fieldName, value) => {
    if (fieldName === "gstCertificate") {
      professionalDetailFormik.setFieldValue("isPhysicalGST", value);
    }
  };

  const handleSaveForm = async (
    addApplicant = false,
    isSaveandPreview = false
  ) => {
    const errors = await professionalDetailFormik.validateForm();

    if (Object.keys(errors).length === 0) {
      const payload = createProfessionalDetailsPayload(false);

      try {
        const res = await dispatch(
          updateApplicantDetailsForm({
            path: route.UPDATE_APPLICANT_DATA,
            payload,
            opportunityId: oppId,
          })
        ).unwrap();

        if (res?.status === 200) {
          dispatch(
            setStepsCompleted({ ...stepsCompleted, [activeStep]: true })
          );
          dispatch(setActiveStep(activeStep + 1));
          dispatch(setShowSaveAndPreview(false));
          if (isSaveandPreview) {
            dispatch(setEditPreviewApplicant(false));
            history.push(`/customer-preview/${oppId}`);
          }
            dispatch(setApplicantActiveStep(0));

          if (addApplicant) {
            dispatch(setApplicantTotal(applicantNo + 1));
            dispatch(setKycVerificationActiveStep(0));
          }
        }
      } catch (err) {
        setSnackbarConfig({
          open: true,
          message: getErrorMessage(err),
          severity: "error",
        });
      }
    } else {
      const touched = Object.keys(errors).reduce(
        (acc, key) => ({ ...acc, [key]: true }),
        {}
      );
      professionalDetailFormik.setTouched(touched);
    }
  };

  const handleSaveAndPreview = () => {
    handleSaveForm(false, true);
  };

  const handleAddApplicant = () => {
    handleSaveForm(true);
  };

  useImperativeHandle(ref, () => ({
    saveUpdatedData: () => {
      const payload = createProfessionalDetailsPayload(false);
      dispatch(
        updateApplicantDetailsForm({
          path: route.UPDATE_APPLICANT_DATA,
          payload,
          opportunityId: oppId,
        })
      );
    },
    errors: professionalDetailFormik.errors,
  }));

  const handlePlaceChange = (data) => {
    professionalDetailFormik.setFieldValue(
      "companyPinCode",
      data?.pinCode ?? ""
    );
  };

  const handleArmedPlaceChange = (data) => {
    professionalDetailFormik.setFieldValue(
      "officePinCode",
      data?.pinCode ?? ""
    );
  };

  const handleSnackbarClose = () => {
    setSnackbarConfig({
      open: false,
      message: "",
      severity: "",
    });
  };

  const renderOccupationFields = (formik, masterData) => {
    const occupation = formik.values.occupation;
    const occupationFieldMap = {
      [OCCUPATION.ARMED_FORCES]: (
        <>
          <Grid item xs={12} sm={6}>
            <SelectInput
              field={{
                name: "branch",
                label: t("professionalDetails.fields.branch"),
                required: true,
                readonly: false,
                // options: masterData?.Branches || [],
                options: BRANCH_OPTIONS || [],
                placeholder: t("professionalDetails.placeholders.branch"),
              }}
              formik={formik}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextFieldInput
              field={{
                name: "rank",
                label: t("professionalDetails.fields.rank"),
                required: true,
                readonly: false,
                placeholder: t("professionalDetails.placeholders.rank"),
                handleOnChange: (e) => {
                  const newValue = e.target.value?.replace(/[^a-zA-Z\s]/g, "");
                  formik.setFieldValue("rank", newValue);
                },
              }}
              formik={formik}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <GoogleMapsAutocomplete
              name="officeAddress"
              formik={professionalDetailFormik}
              label={t("professionalDetails.fields.officeAddress")}
              placeholder={t("professionalDetails.placeholders.officeAddress")}
              variant="outlined"
              TextFieldProps={{ size: "small" }}
              onSelect={(data) => handleArmedPlaceChange(data)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextFieldInput
              field={{
                name: "officePinCode",
                label: t("professionalDetails.fields.officePinCode"),
                placeholder: t(
                  "professionalDetails.placeholders.officePinCode"
                ),
                required: false,
                readonly: false,
                maxLength: 6,
                handleOnChange: (e) => {
                  const newValue = e.target.value
                    ?.replace(/[^0-9]/g, "")
                    .slice(0, 6);
                  professionalDetailFormik.setFieldValue(
                    "officePinCode",
                    newValue
                  );
                },
              }}
              formik={professionalDetailFormik}
            />
          </Grid>
        </>
      ),
    };
    return occupationFieldMap[occupation] || null;
  };

  return (
    <div>
      <form onSubmit={professionalDetailFormik.handleSubmit} autoComplete="off">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <SelectInput
                  field={{
                    name: "occupation",
                    label: t("professionalDetails.fields.occupation"),
                    required: true,
                    readonly: false,
                    options: masterData?.Occupation || [],
                    // options: OCCUPATION_OPTIONS || [],
                    placeholder: t(
                      "professionalDetails.placeholders.occupation"
                    ),
                  }}
                  formik={professionalDetailFormik}
                />
              </Grid>
            </Grid>
          </Grid>
          {/* Industry & company fields */}
          {professionalDetailFormik.values.occupation &&
            [OCCUPATION.EMPLOYED_PRIVATE_SECTOR, OCCUPATION.BUSINESS].includes(
              professionalDetailFormik.values.occupation
            ) && (
              <>
                <Grid item xs={12} sm={6}>
                  <Typography
                    sx={{
                      marginBottom: "6px",
                      fontSize: "14px",
                      fontWeight: 500,
                    }}
                  >
                    {t("professionalDetails.fields.industry")}{" "}
                    <span className="asteriskColor">*</span>
                  </Typography>
                  <SearchableAutoComplete
                    field={{
                      name: "industry",
                      otherField: "industryIfOthers",
                      options: masterData?.Industry || [],
                      placeholder: t(
                        "professionalDetails.placeholders.industry"
                      ),
                      othersValue: OTHERS,
                      required: true,
                      readonly: false,
                    }}
                    formik={professionalDetailFormik}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <TextFieldInput
                      field={{
                        name: "companyName",
                        label: t("professionalDetails.fields.companyName"),
                        placeholder: t(
                          "professionalDetails.placeholders.companyName"
                        ),
                        required: true,
                        readonly: false,
                        handleOnChange: (e) => {
                          const newValue = e.target.value?.replace(
                            /[^a-zA-Z0-9\s]/g,
                            ""
                          );
                          professionalDetailFormik.setFieldValue(
                            "companyName",
                            newValue
                          );
                        },
                      }}
                      formik={professionalDetailFormik}
                    />
                    <Tooltip
                      title={t(
                        "professionalDetails.tooltips.addCompanyAddress"
                      )}
                    >
                      <AddCircleOutlineIcon
                        sx={{
                          color: "#1A407D",
                          marginTop:
                            professionalDetailFormik?.touched?.companyName &&
                            professionalDetailFormik?.errors?.companyName
                              ? "0px"
                              : "28px",
                        }}
                        onClick={() => setShowAddress(true)}
                      />
                    </Tooltip>
                  </Box>
                </Grid>

                {/* Address & Pincode */}
                {showAddress && (
                  <Grid item xs={12} sm={6}>
                    <GoogleMapsAutocomplete
                      name="companyAddress"
                      formik={professionalDetailFormik}
                      label={t("professionalDetails.fields.companyAddress")}
                      placeholder={t(
                        "professionalDetails.placeholders.companyAddress"
                      )}
                      variant="outlined"
                      TextFieldProps={{ size: "small" }}
                      onSelect={(data) => handlePlaceChange(data)}
                    />
                  </Grid>
                )}

                {showAddress && (
                  <Grid item xs={12} sm={6}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                        gap: 1,
                      }}
                    >
                      <TextFieldInput
                        field={{
                          name: "companyPinCode",
                          label: t("professionalDetails.fields.companyPinCode"),
                          placeholder: t(
                            "professionalDetails.placeholders.companyPinCode"
                          ),
                          required: false,
                          readonly: false,
                          maxLength: 6,
                          handleOnChange: (e) => {
                            const newValue = e.target.value
                              ?.replace(/[^0-9]/g, "")
                              .slice(0, 6);
                            professionalDetailFormik.setFieldValue(
                              "companyPinCode",
                              newValue
                            );
                          },
                        }}
                        formik={professionalDetailFormik}
                      />
                      <RemoveCircleIcon
                        sx={{ color: "#ff0000", marginTop: "28px" }}
                        onClick={() => setShowAddress(false)}
                      />
                    </Box>
                  </Grid>
                )}
              </>
            )}

          {/* Designation only for salaried */}
          {(professionalDetailFormik.values.occupation ===
            OCCUPATION.EMPLOYED_PRIVATE_SECTOR ||
            professionalDetailFormik.values.occupation ===
              OCCUPATION.EMPLOYED_GOVERNMENT_SECTOR) && (
            <>
              {" "}
              {professionalDetailFormik.values.occupation ===
                OCCUPATION.EMPLOYED_GOVERNMENT_SECTOR && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <TextFieldInput
                      field={{
                        name: "departmentDivision",
                        label: t(
                          "professionalDetails.fields.departmentDivision"
                        ),
                        placeholder: t(
                          "professionalDetails.placeholders.departmentDivision"
                        ),
                        required: true,
                        readonly: false,
                        handleOnChange: (e) => {
                          const newValue = e.target.value?.replace(
                            /[^a-zA-Z\s]/g,
                            ""
                          );
                          professionalDetailFormik.setFieldValue(
                            "departmentDivision",
                            newValue
                          );
                        },
                      }}
                      formik={professionalDetailFormik}
                    />
                  </Box>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <TextFieldInput
                    field={{
                      name: "designationIfOthers",
                      label: t("professionalDetails.fields.designation"),
                      required: true,
                      readonly: false,
                      placeholder: t(
                        "professionalDetails.placeholders.designation"
                      ),
                      handleOnChange: (e) => {
                        const newValue = e.target.value?.replace(
                          /[^a-zA-Z\s]/g,
                          ""
                        );
                        professionalDetailFormik.setFieldValue(
                          "designationIfOthers",
                          newValue
                        );
                      },
                    }}
                    formik={professionalDetailFormik}
                  />
                  {professionalDetailFormik?.values?.occupation ===
                    OCCUPATION.EMPLOYED_GOVERNMENT_SECTOR && (
                    <Tooltip
                      title={t(
                        "professionalDetails.tooltips.addCompanyAddress"
                      )}
                    >
                      <AddCircleOutlineIcon
                        sx={{
                          color: "#1A407D",
                          marginTop:
                            professionalDetailFormik?.touched
                              ?.departmentDivision &&
                            professionalDetailFormik?.errors?.departmentDivision
                              ? "0px"
                              : "28px",
                        }}
                        onClick={() => setShowOfficeAddress(true)}
                      />
                    </Tooltip>
                  )}
                </Box>
              </Grid>
              {showOfficeAddress &&
                professionalDetailFormik.values.occupation ===
                  OCCUPATION.EMPLOYED_GOVERNMENT_SECTOR && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <GoogleMapsAutocomplete
                        name="officeAddress"
                        formik={professionalDetailFormik}
                        label={t("professionalDetails.fields.officeAddress")}
                        placeholder={t(
                          "professionalDetails.placeholders.officeAddress"
                        )}
                        variant="outlined"
                        TextFieldProps={{ size: "small" }}
                        onSelect={(data) => handleArmedPlaceChange(data)}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          width: "100%",
                          gap: 1,
                        }}
                      >
                        <TextFieldInput
                          field={{
                            name: "officePinCode",
                            label: t(
                              "professionalDetails.fields.officePinCode"
                            ),
                            placeholder: t(
                              "professionalDetails.placeholders.officePinCode"
                            ),
                            required: false,
                            readonly: false,
                            maxLength: 6,
                            handleOnChange: (e) => {
                              const newValue = e.target.value
                                ?.replace(/[^0-9]/g, "")
                                .slice(0, 6);
                              professionalDetailFormik.setFieldValue(
                                "officePinCode",
                                newValue
                              );
                            },
                          }}
                          formik={professionalDetailFormik}
                        />
                        <RemoveCircleIcon
                          sx={{ color: "#ff0000", marginTop: "28px" }}
                          onClick={() => setShowOfficeAddress(false)}
                        />
                      </Box>
                    </Grid>
                  </>
                )}
            </>
          )}
          {/* Armed Forces fields */}
          {renderOccupationFields(professionalDetailFormik, masterData)}

          {/* Annual Income */}
          {professionalDetailFormik.values.occupation && (
            <Grid item xs={12} sm={6}>
              <SelectInput
                field={{
                  name: "annualIncome",
                  label: t("professionalDetails.fields.annualIncome"),
                  options: masterData?.Annual_Household_Income_in_Lakhs,
                  placeholder: t(
                    "professionalDetails.placeholders.annualIncome"
                  ),
                  required: true,
                }}
                formik={professionalDetailFormik}
              />
            </Grid>
          )}

          {/* GST Section: show only after occupation */}
          {professionalDetailFormik.values.occupation && (
            <Grid item xs={12} sx={{ mt: 1, mb: 2 }}>
              <Grid container spacing={3} sx={{ alignItems: "center" }}>
                <Grid item>
                  <Typography sx={{ fontSize: "14px" }}>
                    {t("professionalDetails.tooltips.gstInfoText")}
                  </Typography>
                </Grid>
                <Grid item>
                  <SwitchButton
                    checked={professionalDetailFormik?.values?.isGstClaimed}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      professionalDetailFormik.setFieldValue(
                        "isGstClaimed",
                        isChecked
                      );

                      // Reset fields if unchecked
                      if (!isChecked) {
                        professionalDetailFormik.setFieldValue(
                          "gstLegalName",
                          ""
                        );
                        professionalDetailFormik.setFieldValue(
                          "gstBusinessName",
                          ""
                        );
                        professionalDetailFormik.setFieldValue("gstNumber", "");
                        professionalDetailFormik.setFieldValue(
                          "gstCertificate",
                          []
                        );
                        professionalDetailFormik.setFieldValue(
                          "isPhysicalGST",
                          false
                        );
                      }
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          )}

          {professionalDetailFormik?.values?.isGstClaimed && (
            <>
              <Grid container spacing={2} sx={{ mb: 2, ml: 0 }}>
                <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
                  <Box sx={{ display: "flex", alignItems: "inherit", gap: 2 }}>
                    <Box>
                      <TextFieldInput
                        field={{
                          name: "gstNumber",
                          placeholder: t(
                            "professionalDetails.placeholders.gstNumber"
                          ),
                          label: t("companyDetails.labels.gstNumber"),
                          required: true,
                          readonly: false,
                          showClearIcon: true,
                          onClear: () => {
                            // Clear dependent fields
                            professionalDetailFormik.setValues({
                              ...professionalDetailFormik.values,
                              gstNumber: "",
                              gstBusinessName: "",
                              gstLegalName: "",
                              gstCertificate: [],
                              isPhysicalGST: false,
                              fetchedGSTDetails: false,
                            });
                            // Reset gstDetailsData in Redux
                            dispatch(resetGstDetailsData());
                          },
                        }}
                        formik={professionalDetailFormik}
                      />
                      {gstDetailsData?.kycStatus === "FAILURE" ? (
                        <Box sx={{ display: "flex", mt: 0.5 }}>
                          <img src={warningIcon} alt="warning-icon" />
                          <Typography
                            sx={{
                              fontSize: "12px",
                              color: "#FF5630",
                              ml: 0.5,
                            }}
                          >
                            {t(
                              "professionalDetails.validation.invalidGstNumber"
                            )}
                          </Typography>
                        </Box>
                      ) : null}
                    </Box>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleFetchCompanyDetails}
                      disabled={
                        professionalDetailFormik?.values?.fetchedGSTDetails ||
                        !professionalDetailFormik.values?.gstNumber || // disable if GST is empty
                        !!gstDetailsData?.kycResult // or if already fetched
                      }
                      sx={{
                        backgroundColor: "#1A407D",
                        textTransform: "none",
                        fontSize: "14px",
                        borderRadius: "8px",
                        fontWeight: 400,
                        "&:hover": {
                          backgroundColor: "#1A407D",
                        },
                        minWidth: "120px",
                        height: "45px",
                        mt: 3.4,
                      }}
                    >
                      {isButtonLoading ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        t("professionalDetails.fields.getDetails")
                      )}
                    </Button>
                  </Box>
                </Grid>

                {professionalDetailFormik?.values?.gstBusinessName ||
                professionalDetailFormik?.values?.gstLegalName ? (
                  <Grid item xs={12} sm={6}>
                    <TextFieldInput
                      field={{
                        name: "gstBusinessName",
                        label: t("professionalDetails.fields.gstBusinessName"),
                        required:
                          !professionalDetailFormik?.values?.isPhysicalGST,
                        readonly: true,
                        placeholder: t(
                          "professionalDetails.placeholders.gstBusinessName"
                        ),
                      }}
                      formik={professionalDetailFormik}
                    />
                  </Grid>
                ) : null}
              </Grid>

              {professionalDetailFormik?.values?.gstBusinessName ||
              professionalDetailFormik?.values?.gstLegalName ? (
                <Grid container spacing={2} sx={{ mb: 2, ml: 0 }}>
                  <Grid item xs={12} sm={6}>
                    <TextFieldInput
                      field={{
                        name: "gstLegalName",
                        label: t("professionalDetails.fields.gstLegalName"),
                        required:
                          !professionalDetailFormik?.values?.isPhysicalGST,
                        readonly: true,
                        placeholder: t(
                          "professionalDetails.placeholders.gstLegalName"
                        ),
                      }}
                      formik={professionalDetailFormik}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <UploadInput
                      sx={{ width: "100%" }}
                      field={{
                        name: "gstCertificate",
                        label: t("professionalDetails.fields.gstCertificate"),
                        required:
                          !professionalDetailFormik?.values?.isPhysicalGST,
                        readonly: false,
                        placeholder: professionalDetailFormik?.values
                          ?.isPhysicalGST
                          ? t(
                              "professionalDetails.placeholders.gstCertificatePhysical"
                            )
                          : t(
                              "professionalDetails.placeholders.uploadGstCertificate"
                            ),
                        placeholderUpload: t("commonPlaceholders.uploaded"),
                        handleOnClick: handleShowUploadGSTCertificate,
                        physicalCopyValue:
                          professionalDetailFormik?.values?.isPhysicalGST,
                      }}
                      formik={professionalDetailFormik}
                    />
                  </Grid>
                </Grid>
              ) : null}
            </>
          )}
          <AddAuthorizedSignatoryButton
            applicantNo={applicantNo}
            applicantTotal={applicantTotal}
            handleAddAuthorizedSignatory={handleAddApplicant}
            type="applicant"
          />
        </Grid>
        {showUploadGSTPopup && (
          <UploadPopup
            heading="Upload Your GST Certificate"
            totalImages={1}
            closePopup={() => setShowUploadGSTPopup(false)}
            handleImageValue={handleImageValue}
            uploadedImage={
              professionalDetailFormik?.values?.gstCertificate || []
            }
            fieldName="gstCertificate"
            handleUpdateImageData={handleUpdateImageData}
            handlePhysicalImage={handlePhysicalImage}
            selectedOptionValue={
              professionalDetailFormik?.values?.isPhysicalGST
            }
            fileType="both"
            isGSTUpload={true}
          />
        )}
        <CommonButton
          handlePrevious={() =>
            dispatch(setApplicantActiveStep(applicantActiveStep - 1))
          }
          handleNext={() => handleSaveForm(false)}
          saveAndPreview={handleSaveAndPreview}
          saveDraftPath={route.UPDATE_APPLICANT_DATA}
          saveDraftData={createProfessionalDetailsPayload(true)}
          innerSteps={true}
          applicantActiveStep={applicantActiveStep}
          fromPage="professional"
          applicantNo={applicantNo}
        />
      </form>
      <SnackbarAlert
        open={snackbarConfig.open}
        message={snackbarConfig.message}
        severity={snackbarConfig.severity}
        onClose={handleSnackbarClose}
      />
    </div>
  );
});

export default ProfessionalDetails;
